import React, { useEffect, useState, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { addPartner, getPartners } from 'actions/partnerActions';
import { debounce } from '@mui/material';
import { Pagination } from '@mui/lab';
import { Notify, SUCCESS_NOTIF, ERROR_NOTIF } from './components/Notify';
import { UpdateItem } from './components/UpdateItem';
import { Partner } from '@m4a3/api-client';
import { EnhancedTableToolbar } from './components/EnhancedTableToolbar';
import { EnhancedTableHead } from './components/EnhanchedTableHead';
import { TableLoading } from './components/TableLoading';

interface Notification {
  type: string;
  variant: string;
  message: string;
  id?: string;
}

export default function EnhancedTable({ cellId }) {
  const [selected, setSelected] = React.useState([]);
  const [fetchingPartners, setFetchingPartners] = useState(true);
  const [partners, setPartners] = useState<Partner[]>();
  const [showAddItem, setShowAddItem] = useState(false);
  const [filterType, setFilterType] = useState();
  const [page, setPage] = useState(1);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [notification, setNotification] = useState<Notification>();
  const [searchTerm, setSearchTerm] = useState();

  const partnersRef = useRef([]);

  const refreshPartners = async () => {
    try {
      setFetchingPartners(true);
      const partners = await getPartners();
      setPartners(partners);
      partnersRef.current = partners;
      runFilter();
      if (searchTerm) {
        search(searchTerm);
      }
      setFetchingPartners(false);
    } catch (err) {
      setFetchingPartners(false);
    }
  };

  useEffect(() => {
    refreshPartners();
  }, [cellId]);

  const partnerFilterCheck = partner => {
    if (selectedPartners.length > 0) {
      return selectedPartners.includes(partner);
    } else {
      return true;
    }
  };

  const runFilter = () => {
    const filteredPartners = partnersRef.current.filter(partner => {
      let partnerNameFilter = partnerFilterCheck(partner.partnerName);

      return partnerNameFilter;
    });
    setPartners(filteredPartners);
  };

  const openFilterType = filter => {
    setFilterType(filter);
  };

  const search = searchTerm => {
    const filteredPartners = partnersRef.current.filter(p => {
      return p.partnerName.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setSearchTerm(searchTerm);
    setPartners(filteredPartners);
    setPage(1);
    setSelected([]);
  };

  const onSearch = debounce(search, 300);

  const handleClick = (event, selectedPartner) => {
    try {
      const selectedIndex = selected.findIndex(
        p => p.partnerName === selectedPartner.partnerName
      );
      let newSelected = [];
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, selectedPartner);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    } catch (err) {
      console.error(err);
    }
  };

  const onAddPartner = async partner => {
    setShowAddItem(false);
    try {
      await addPartner(partner);
      refreshPartners();
      launchNotification({ type: SUCCESS_NOTIF, message: 'Added sucessfully' });
    } catch (err) {
      launchNotification({
        type: ERROR_NOTIF,
        message: 'Failed to add partner'
      });
    }
  };

  const launchNotification = notification => {
    setNotification(notification);
    setTimeout(() => {
      setNotification(null);
    }, 2000);
  };

  const isSelected = name => {
    return selected.findIndex(p => name === p.partnerName) !== -1;
  };

  const partnersRowsStartIndex = (page - 1) * 20;
  const partnerstRowsEndIndex = partnersRowsStartIndex + 20;
  return (
    <div style={{ width: '100%' }}>
      <Paper
        sx={{ mb: 2 }}
        style={{ position: 'relative', top: 0, left: 0, width: '100%' }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          onAddItem={() => {
            setShowAddItem(true);
          }}
          openFilter={openFilterType}
          onSearch={onSearch}
        />
        <TableContainer
          style={{
            height: Math.round(window.innerHeight * 0.65),
            overflow: 'auto'
          }}>
          <Table sx={{ minWidth: 750 }} size={'medium'}>
            <EnhancedTableHead />
            {!partners && (
              <>
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
                <TableLoading />
              </>
            )}
            <TableBody style={{ height: 500, overflowY: 'auto' }}>
              {partners &&
                partners
                  .slice(partnersRowsStartIndex, partnerstRowsEndIndex)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.partnerName);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={event => handleClick(event, row)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={`${row.partnerName}_${index}`}
                        selected={isItemSelected}>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.partnerName}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
        {partners && partners.length > 20 && (
          <div style={{ padding: 20, borderTop: '1px solid #efefef' }}>
            <Pagination
              count={Math.round((partners.length + 1) / 20)}
              variant="outlined"
              shape="rounded"
              page={page}
              onChange={(evt, val) => {
                setPage(val);
              }}
            />
          </div>
        )}
        {showAddItem && (
          <UpdateItem
            onClose={() => {
              setShowAddItem(false);
            }}
            partners={partners}
            onSubmit={onAddPartner}
            edit={undefined}
          />
        )}

        {notification && (
          <Notify type={notification.type} message={notification.message} />
        )}
      </Paper>
    </div>
  );
}
